import { useMutation, useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import Button from "components/base/Button";
import CertificadoFormCard from "components/cards/CertificadoFormCard";
import EditarCertificadoFormCard from "components/cards/EditarCertificadoFormCard";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import {
  AlterarEmpresaDtoType,
  empresaBreadcrumbItems,
  regimesTributarios,
} from "data/empresa";
import { transformErrors } from "helpers/utils";
import { useEffect } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Navigate, useParams } from "react-router-dom";
import { usePermissionModal } from "contexts/PermissionModalContext";

type UsuarioSngpcType = {
  id: string;
  nome: string;
  email: string;
};

const EditarEmpresa = () => {
  const { id } = useParams();
  const { modalState, getQueryRetryHandler, getMutationErrorHandler } = usePermissionModal();

  const { isLoading, isError, data, failureReason } = useQuery<{
    data: AlterarEmpresaDtoType;
  }, AxiosError>({
    queryKey: ["empresa", id],
    queryFn: () => axios.get(`/api/empresa/${id}`),
    enabled: !!id,
    retry: getQueryRetryHandler({
      queryKey: ["empresa", id],
      defaultMessage: "Você não possui permissão para visualizar esta empresa"
    }),
  });

  const { data: usuariosSngpc } = useQuery<UsuarioSngpcType[]>({
    queryKey: ["usuarios-sngpc"],
    queryFn: () => axios.get("/api/usuario/sngpc").then((r) => r.data),
  });

  const mutation = useMutation<
    any,
    any,
    { id: string; data: AlterarEmpresaDtoType }
  >({
    mutationFn: ({ id, data }) => axios.put(`/api/empresa/${id}`, data),
    onError: getMutationErrorHandler(
      "Você não possui permissão para editar esta empresa",
      (error) => {
        // Tratamento de erros não relacionados à permissão
        const formattedErrors = transformErrors(error.response?.data?.errors || {});
        for (const [field, error] of Object.entries(formattedErrors)) {
          form.setError(field as any, error as any);
        }
      }
    ),
  });

  const empresa = data?.data;

  const form = useForm<AlterarEmpresaDtoType>();

  useEffect(() => {
    if (empresa && usuariosSngpc) {
      form.reset(empresa);
    }
  }, [empresa, usuariosSngpc]);

  if (isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );

  if (isError && !modalState.isOpen) {
    return <div>Ocorreu um erro ao carregar a empresa</div>;
  }

  if (!empresa && !modalState.isOpen) {
    return <div>Empresa não encontrada</div>;
  }

  const onSubmit: SubmitHandler<AlterarEmpresaDtoType> = (data) => {
    mutation.mutate({ data, id: (id as string) });
  };

  if (mutation.isSuccess) {
    return <Navigate to={`/cadastros/empresas`} />;
  }

  return (
    <div>
      <FormProvider {...form}>
        <PageBreadcrumb items={empresaBreadcrumbItems(empresa?.codigo)} />
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Editar uma empresa</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Atualize as informações da empresa
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="phoenix-secondary"
                type="button"
                onClick={() => form.reset()}
              >
                Descartar
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={mutation.isPending}
                disabled={mutation.isPending}
                loadingPosition="start"
              >
                Salvar
              </Button>
            </div>
          </div>
          <Row className="g-5">
            <Col xs={12} xl={8}>
              <h4 className="mb-3">Identificação da empresa</h4>
              <Row className="g-2 mb-3">
                <Form.Group as={Col} xs={12} xl={2} controlId="cnpj">
                  <Form.Control
                    placeholder="CNPJ"
                    {...form.register("cnpj", {
                      required: "Campo obrigatório",
                    })}
                    required
                    isInvalid={!!form.formState.errors.cnpj}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.cnpj?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} xl={4} controlId="razaoSocial">
                  <Form.Control
                    placeholder="Razão social"
                    {...form.register("razaoSocial")}
                    required
                    isInvalid={!!form.formState.errors.razaoSocial}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.razaoSocial?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} xl={4} controlId="nomeFantasia">
                  <Form.Control
                    placeholder="Nome fantasia"
                    {...form.register("nomeFantasia")}
                    required
                    isInvalid={!!form.formState.errors.nomeFantasia}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.nomeFantasia?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  xs={12}
                  xl={2}
                  controlId="inscricaoEstadual"
                >
                  <Form.Control
                    placeholder="Inscrição estadual"
                    {...form.register("inscricaoEstadual")}
                    required
                    isInvalid={!!form.formState.errors.inscricaoEstadual}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.inscricaoEstadual?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  xs={12}
                  xl={2}
                  controlId="regimeTributario"
                >
                  <Form.Select {...form.register("regimeTributario")}>
                    {regimesTributarios.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.regimeTributario?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <h4 className="mb-3">Dados de contato</h4>
              <Row className="g-2 mb-3">
                <Form.Group as={Col} xs={12} xl={2} controlId="telefone">
                  <Form.Control
                    placeholder="Telefone"
                    {...form.register("telefone")}
                    required
                    isInvalid={!!form.formState.errors.telefone}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.telefone?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} xl={4} controlId="email">
                  <Form.Control
                    type="email"
                    placeholder="E-mail"
                    {...form.register("email")}
                    required
                    isInvalid={!!form.formState.errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.email?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} xl={6} controlId="responsavel">
                  <Form.Control
                    placeholder="Responsável"
                    {...form.register("responsavel")}
                    required
                    isInvalid={!!form.formState.errors.responsavel}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.responsavel?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <h4 className="mb-3">Endereço</h4>
              <Row className="g-2 mb-3">
                <Form.Group as={Col} xs={12} xl={2} controlId="endereco.cep">
                  <Form.Control
                    placeholder="CEP"
                    {...form.register("endereco.cep")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.cep}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.cep?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  xs={8}
                  xl={8}
                  controlId="endereco.logradouro"
                >
                  <Form.Control
                    placeholder="Logradouro"
                    {...form.register("endereco.logradouro")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.logradouro}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.logradouro?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={4} xl={2} controlId="endereco.numero">
                  <Form.Control
                    placeholder="Número"
                    {...form.register("endereco.numero")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.numero}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.numero?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  as={Col}
                  xs={12}
                  xl={4}
                  controlId="endereco.complemento"
                >
                  <Form.Control
                    placeholder="Complemento"
                    {...form.register("endereco.complemento")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.complemento}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.complemento?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={6} xl={4} controlId="endereco.bairro">
                  <Form.Control
                    placeholder="Bairro"
                    {...form.register("endereco.bairro")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.bairro}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.bairro?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  xs={6}
                  xl={4}
                  controlId="endereco.municipio"
                >
                  <Form.Control
                    placeholder="Município"
                    {...form.register("endereco.municipio")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.municipio}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.municipio?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} xs={6} xl={2} controlId="endereco.uf">
                  <Form.Control
                    placeholder="UF"
                    {...form.register("endereco.uf")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.uf}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.uf?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <h4 className="mb-3">SNGPC</h4>
              <Row className="g-2 mb-3">
                <Form.Group as={Col} xs={12} xl={6} controlId="responsavelTecnicoId">
                  <Form.Label>Responsável Técnico SNGPC</Form.Label>
                  <Form.Select {...form.register("responsavelTecnicoId")}>
                    <option value="">Selecione...</option>
                    {(usuariosSngpc ?? []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.nome} {u.email ? `(${u.email})` : ""}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-body-secondary">
                    Farmacêutico responsável pelas transmissões SNGPC
                  </Form.Text>
                </Form.Group>
              </Row>
            </Col>
            <Col xs={12} xl={4}>
              <Row className="g-2">
                <Col xs={12} xl={12}>
                  <EditarCertificadoFormCard className="mb-3" />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </FormProvider>

    </div>
  );
};

export default EditarEmpresa;
